/* @license
 * Copyright 2019 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the 'License');
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an 'AS IS' BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { DoubleSide, LinearFilter, Mesh, MeshStandardMaterial, Object3D, PlaneGeometry, SphereGeometry, SRGBColorSpace, VideoTexture, } from 'three';
export const VideoProjection = {
    Flat: 'flat',
    Equirectangular360: '360-equirectangular',
};
/**
 * Video object that can display videos in a 3D scene.
 * Supports flat and 360-degree equirectangular projections.
 */
export class VideoObject extends Object3D {
    constructor() {
        super();
        this._src = null;
        this._projection = VideoProjection.Flat;
        this._videoElement = null;
        this._videoTexture = null;
        const geometry = new PlaneGeometry();
        const material = new MeshStandardMaterial();
        material.side = DoubleSide;
        // Configure for unlit appearance (like MeshBasicMaterial)
        material.emissiveIntensity = 1.0;
        material.roughness = 1.0;
        material.metalness = 0.0;
        this._mesh = new Mesh(geometry, material);
        this._mesh.name = 'VideoMesh';
        this.add(this._mesh);
        this._mesh.visible = false;
    }
    get src() {
        return this._src;
    }
    set src(src) {
        if (src) {
            this.load(src).catch(console.error);
        }
    }
    get videoElement() {
        return this._videoElement;
    }
    get projection() {
        return this._projection;
    }
    set projection(projection) {
        if (projection === this._projection) {
            return;
        }
        const material = new MeshStandardMaterial();
        // Configure for unlit appearance (like MeshBasicMaterial)
        material.emissiveIntensity = 1.0;
        material.roughness = 1.0;
        material.metalness = 0.0;
        let geometry;
        if (projection === VideoProjection.Equirectangular360) {
            geometry = new SphereGeometry(1, 64, 32);
            // Invert the geometry on the x-axis so that all of the faces point inward
            geometry.scale(-1, 1, 1);
        }
        else {
            geometry = new PlaneGeometry();
            material.side = DoubleSide;
        }
        if (this._videoTexture) {
            material.map = this._videoTexture;
            material.emissiveMap = this._videoTexture;
        }
        this._projection = projection;
        const nextMesh = new Mesh(geometry, material);
        nextMesh.name = 'VideoMesh';
        nextMesh.visible = this._mesh.visible;
        const meshIndex = this.children.indexOf(this._mesh);
        if (meshIndex === -1) {
            this.add(nextMesh);
        }
        else {
            this.children.splice(meshIndex, 1, nextMesh);
            nextMesh.parent = this;
        }
        this._mesh = nextMesh;
        this.onResize();
    }
    loadVideo(src) {
        return new Promise((resolve, reject) => {
            const video = document.createElement('video');
            video.crossOrigin = 'anonymous';
            video.playsInline = true;
            video.muted = true; // Start muted for autoplay
            video.loop = true;
            video.preload = 'auto';
            const onLoadedData = async () => {
                try {
                    // Ensure video is at the start
                    video.currentTime = 0;
                    // Wait for seeked event to ensure frame is ready
                    await new Promise((seekResolve) => {
                        const onSeeked = () => {
                            video.removeEventListener('seeked', onSeeked);
                            seekResolve();
                        };
                        video.addEventListener('seeked', onSeeked);
                        // Fallback timeout
                        setTimeout(() => {
                            video.removeEventListener('seeked', onSeeked);
                            seekResolve();
                        }, 1000);
                    });
                    // Force load first frame by playing briefly then pausing
                    // This ensures the video texture has a valid frame to display
                    try {
                        await video.play();
                        // Play for a very short time to load the first frame
                        await new Promise(resolve => setTimeout(resolve, 50));
                        video.pause();
                        video.currentTime = 0;
                        // Wait for pause and seek to complete
                        await new Promise((pauseResolve) => {
                            const onPauseSeeked = () => {
                                video.removeEventListener('seeked', onPauseSeeked);
                                pauseResolve();
                            };
                            video.addEventListener('seeked', onPauseSeeked);
                            setTimeout(() => {
                                video.removeEventListener('seeked', onPauseSeeked);
                                pauseResolve();
                            }, 500);
                        });
                    }
                    catch (playError) {
                        // If play fails (autoplay restrictions), continue anyway
                        // The seeked event should have loaded the frame
                        console.warn('Could not play video to load first frame:', playError);
                    }
                    // Additional wait to ensure video frame is rendered
                    await new Promise(resolve => requestAnimationFrame(resolve));
                    await new Promise(resolve => requestAnimationFrame(resolve));
                    cleanup();
                    const texture = new VideoTexture(video);
                    texture.minFilter = LinearFilter;
                    texture.colorSpace = SRGBColorSpace;
                    // Force texture update to show first frame
                    texture.needsUpdate = true;
                    resolve(texture);
                }
                catch (error) {
                    cleanup();
                    reject(error);
                }
            };
            const onError = (error) => {
                cleanup();
                reject(new Error(`Error loading video "${src}": ${error}`));
            };
            const cleanup = () => {
                video.removeEventListener('loadeddata', onLoadedData);
                video.removeEventListener('error', onError);
            };
            video.addEventListener('loadeddata', onLoadedData);
            video.addEventListener('error', onError);
            video.src = src;
            video.load();
        });
    }
    async load(src) {
        this._src = src;
        this._mesh.visible = false;
        const material = this._mesh.material;
        // Clean up old video
        if (this._videoElement) {
            this._videoElement.pause();
            this._videoElement.src = '';
            this._videoElement.load();
        }
        if (material.map) {
            material.map.dispose();
        }
        if (material.emissiveMap && material.emissiveMap !== material.map) {
            material.emissiveMap.dispose();
        }
        this._videoTexture = await this.loadVideo(src);
        this._videoElement = this._videoTexture.image;
        this.onResize();
        material.map = this._videoTexture;
        material.emissiveMap = this._videoTexture;
        material.needsUpdate = true;
        this._mesh.visible = true;
        return this;
    }
    play() {
        if (this._videoElement) {
            return this._videoElement.play();
        }
        return Promise.resolve();
    }
    pause() {
        if (this._videoElement) {
            this._videoElement.pause();
        }
    }
    get paused() {
        var _a, _b;
        return (_b = (_a = this._videoElement) === null || _a === void 0 ? void 0 : _a.paused) !== null && _b !== void 0 ? _b : true;
    }
    get muted() {
        var _a, _b;
        return (_b = (_a = this._videoElement) === null || _a === void 0 ? void 0 : _a.muted) !== null && _b !== void 0 ? _b : true;
    }
    set muted(value) {
        if (this._videoElement) {
            this._videoElement.muted = value;
        }
    }
    get currentTime() {
        var _a, _b;
        return (_b = (_a = this._videoElement) === null || _a === void 0 ? void 0 : _a.currentTime) !== null && _b !== void 0 ? _b : 0;
    }
    set currentTime(value) {
        if (this._videoElement) {
            this._videoElement.currentTime = value;
        }
    }
    get duration() {
        var _a, _b;
        return (_b = (_a = this._videoElement) === null || _a === void 0 ? void 0 : _a.duration) !== null && _b !== void 0 ? _b : 0;
    }
    get loop() {
        var _a, _b;
        return (_b = (_a = this._videoElement) === null || _a === void 0 ? void 0 : _a.loop) !== null && _b !== void 0 ? _b : true;
    }
    set loop(value) {
        if (this._videoElement) {
            this._videoElement.loop = value;
        }
    }
    onResize() {
        if (this._videoElement && this.projection === VideoProjection.Flat) {
            const ratio = (this._videoElement.videoHeight || 1.0) /
                (this._videoElement.videoWidth || 1.0);
            const width = Math.min(1.0, 1.0 / ratio);
            const height = Math.min(1.0, ratio);
            this._mesh.scale.set(width, height, 1);
        }
    }
    copy(source, recursive = true) {
        if (recursive) {
            this.remove(this._mesh);
        }
        super.copy(source, recursive);
        if (recursive) {
            const _meshIndex = source.children.indexOf(source._mesh);
            if (_meshIndex !== -1) {
                this._mesh = this.children[_meshIndex];
            }
        }
        this.projection = source.projection;
        if (source.src) {
            this.src = source.src;
        }
        return this;
    }
    dispose() {
        if (this._videoElement) {
            this._videoElement.pause();
            this._videoElement.src = '';
            this._videoElement.load();
            this._videoElement = null;
        }
        const material = this._mesh.material;
        if (material.map) {
            material.map.dispose();
        }
        if (material.emissiveMap && material.emissiveMap !== material.map) {
            material.emissiveMap.dispose();
        }
        material.dispose();
        this._mesh.geometry.dispose();
        this.remove(this._mesh);
        this._videoTexture = null;
    }
    /**
     * Returns true if the video needs continuous rendering
     */
    needsContinuousRendering() {
        return this._videoElement !== null && !this._videoElement.paused;
    }
}
//# sourceMappingURL=VideoObject.js.map